// src/portal/PortalFindingsPanel.jsx
//
// PORTED from the mock's `.findings` — the privacy line, and one `.finding`
// card per finding carrying a numbered pin, the kind chip, the note and a
// target/author footer.
//
// ── THE PIN CARRIES THE NUMBER AND NOT THE KIND, AND THAT IS MEASURED ──────
//
// The mock colours pins by kind: --danger for setback_violation, --info for
// reroute_required, --warn for surface_conflict. Its README also says those
// numbered circles become MAP MARKERS — which puts them on a surface that
// already carries five meanings with a collision history (§3.22, M10, a palette
// pinned by tests so "they differ now" cannot be satisfied by moving the wrong
// one).
//
// Measured against the five geometry roles, CIEDE2000:
//
//   --warn   (surface_conflict)  vs geometry ROAD      3.67   <-- collision
//   --danger (setback_violation) vs geometry MARKUP   20.74
//   --info   (reroute_required)  vs geometry OTHER    21.54
//
// The palette's own minimum separation is 17.75, and the chrome collision
// retired in phase 13 was 6.99. A surface_conflict pin would be a road. So the
// pin takes --geom-markup, the colour a reviewer's annotation ALREADY has on
// that map, and the KIND is carried by the chip in the list where there is no
// geometry to collide with.
import { findingNumber, findingKind } from "./portalFindingNumber";

/** The mock's kind styles. In the LIST only — see the note above. */
const KIND_CLASS = {
  setback_violation:   "text-[#FF8A8E] bg-[rgba(242,85,90,0.12)] border-[rgba(242,85,90,0.38)]",
  reroute_required:    "text-[#7CC8EF] bg-[rgba(58,174,232,0.12)] border-[rgba(58,174,232,0.38)]",
  surface_conflict:    "text-[#F8BE5C] bg-[rgba(245,165,36,0.12)] border-[rgba(245,165,36,0.38)]",
  information_missing: "text-fg-primary bg-[rgba(143,178,214,0.10)] border-bg-border",
  other:               "text-fg-secondary bg-transparent border-bg-border",
};

const timeOf = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
};

export default function PortalFindingsPanel({ markup = [], role, features = [] }) {
  const byOrdinal = new Map((features ?? []).map((f) => [f.ordinal, f]));

  return (
    <section className="op-panel" aria-label="Findings" data-testid="findings-panel">
      <div className="op-panel-head">
        <div className="flex items-center gap-2">
          <span className="op-panel-title">Findings</span>
          {markup.length > 0 && (
            <span className="op-chip op-chip--accent" data-testid="findings-count">
              {markup.length}
            </span>
          )}
        </div>
        {/* LOAD-BEARING COPY, not decoration — the README says so, and it has
            been pinned since the thread commit. The submitter is NOT told this,
            because it is not true for them: what they can see has already been
            returned. */}
        {role === "reviewer" && (
          <span className="op-label" data-testid="findings-privacy">
            Private to you until you return the plan
          </span>
        )}
      </div>

      <div className="op-panel-body gap-2">
        {markup.length === 0 ? (
          <p className="op-meta">
            {role === "reviewer"
              ? "No findings yet. Draw on the plan to add one."
              : "The reviewer has not returned any findings."}
          </p>
        ) : (
          markup.map((m) => {
            const n = findingNumber(m);
            const kind = findingKind(m);
            const feature = byOrdinal.get(m.feature_ordinal);
            return (
              <article
                key={m.id}
                data-testid={`finding-${m.id}`}
                className="flex flex-col gap-1.5 rounded-[--r-lg] border border-bg-border bg-bg p-2.5"
              >
                <div className="flex items-center gap-2">
                  {/* The pin. Number only — see the header note. */}
                  <span
                    data-testid={`finding-pin-${m.id}`}
                    className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-[rgb(var(--geom-markup))] font-mono text-[11px] font-bold text-[#0C0E10]"
                  >
                    {n}
                  </span>
                  {kind && (
                    <span
                      data-testid={`finding-kind-${m.id}`}
                      className={`op-chip ${KIND_CLASS[kind] ?? KIND_CLASS.other}`}
                    >
                      {/* THE LITERAL ENUM VALUE. setback_violation, not
                          "Setback violation" — the string in the chip is the
                          string in the record, so quoting one quotes the
                          other. */}
                      {kind}
                    </span>
                  )}
                  {role === "reviewer" && !m.frozen_at && (
                    <span className="op-label text-accent-action">STAGED</span>
                  )}
                </div>

                <p className="whitespace-pre-wrap text-xs text-fg-primary">{m.note}</p>

                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <span className="op-label" data-testid={`finding-target-${m.id}`}>
                    {[feature?.submitter_tag ?? `FEATURE ${m.feature_ordinal}`,
                      feature?.geom_class].filter(Boolean).join(" · ").toUpperCase()}
                  </span>
                  <span className="op-meta">
                    {[m.author_username_at_write, timeOf(m.created_at)].filter(Boolean).join(" · ")}
                  </span>
                </div>
              </article>
            );
          })
        )}
      </div>
    </section>
  );
}
